import {
  MIME_TYPE_JSON_LD,
  MIME_TYPE_MERGE_PATCH_JSON,
} from "@rumble-it/common-lib/src/constants/mimeTypes";
import { axiosInstance } from "../client";
import {
  RootAffiliateServicePartnersOut,
  ServicePartner,
} from "@rumble-it/common-lib/types/models/ServicePartner.interface";
import { CollectionResponse } from "@rumble-it/common-lib/types/api/CollectionResponse.interface";
import { PaginationParams } from "@rumble-it/common-lib/types/api/PaginationParams.interface";
import { Brand } from "@rumble-it/common-lib/types/common/Brand.interface";
import { ServicepartnerUpdateDto } from "../../../pages/admin/servicepartners/components/ServicepartnerForm";
import { ServicePartnerRootImportAssignValues } from "../../../pages/admin/servicepartners/components/ServicepartnerRootImportForm";

// Brief endpoint item shape after axios interceptor normalization.
export type ServicePartnerBrief = Pick<
  ServicePartner,
  "id" | "fullName" | "groupName" | "name" | "city"
>;

interface ServicepartnerFilterParams {
  name?: string;
  [key: string]: any;
}

export interface ServicepartnerQueryParams
  extends PaginationParams,
    ServicepartnerFilterParams {}

export type DwProAccount = {
  id: string;
  title: string;
  "@id"?: string;
  stage?: string;
};

export const fetchServicepartners = async (
  params: ServicepartnerQueryParams,
): Promise<CollectionResponse<ServicePartner>> =>
  axiosInstance
    .get("/service-partners", { params })
    .then((response) => response.data);

export const fetchServicepartnersWithoutPagination = async (): Promise<
  ServicePartnerBrief[]
> =>
  axiosInstance
    .get(`/admin/service-partners/brief`)
    .then((response) => response.data);

export const fetchServicePartnerById = async (
  id: string,
): Promise<ServicePartner> =>
  axiosInstance
    .get(`/admin/service-partners/${id}`)
    .then((response) => response.data);

export const createServicePartner = async (
  data: Partial<ServicePartner>,
): Promise<null> =>
  axiosInstance
    .post(`/service-partners`, data)
    .then((response) => response.data);

export const updateServicePartnerById = async (
  id: string,
  data: ServicepartnerUpdateDto,
): Promise<null> =>
  axiosInstance
    .patch(
      `/admin/service-partners/${id}`,
      { ...data },
      {
        headers: { "Content-Type": MIME_TYPE_MERGE_PATCH_JSON },
      },
    )
    .then((response) => response.data);

// Create change-request for a service partner (JSON-LD payload)
export const createServicePartnerChangeRequest = async (
  id: string,
  data: Partial<ServicePartner>,
): Promise<null> =>
  axiosInstance
    .post(`/service-partners/${id}/change-request`, data, {
      headers: { "Content-Type": MIME_TYPE_JSON_LD },
    })
    .then((response) => response.data);

// Vehicle brands helper (temporary until a dedicated module exists)
export const fetchVehicleBrands = async (): Promise<
  CollectionResponse<Brand>
> =>
  axiosInstance.get(`/admin/vehicle-brands`).then((response) => response.data);

export const fetchDwProAccounts = async (): Promise<
  CollectionResponse<DwProAccount> | DwProAccount[]
> =>
  axiosInstance
    .get(`/dw-pro/accounts`, { params: { pagination: false } })
    .then((response) => response.data);

export const fetchDwProAccountById = async (
  id: string,
): Promise<DwProAccount> =>
  axiosInstance.get(`/dw-pro/accounts/${id}`).then((response) => response.data);

export const fetchRootServicePartnersByAffiliateId = async (
  affiliateId: string,
): Promise<RootAffiliateServicePartnersOut> =>
  axiosInstance
    .get(`/service-partners/affiliate/${affiliateId}`, {
      isRootRequest: true,
    })
    .then((response) => response.data);

export const assignRootServicePartners = async (
  affiliateId: string,
  data: ServicePartnerRootImportAssignValues,
): Promise<null> =>
  axiosInstance
    .post(`/service-partners/affiliate/${affiliateId}/assign`, data, {
      isRootRequest: true,
    })
    .then((response) => response.data);
